import * as PIXI from 'pixi.js';
import sum from 'lodash/sum';

import type { OpaqueAsset } from '../../video-data/types';
import type { BufferingMode } from '../../enums';
import { CreationType, DynamicStylingCreationLevel, TextAlignment } from '../../enums';
import {
    assertArray,
    assertInArray,
    assertInEnum,
    assertMaximum,
    assertMinimum,
    assertNumber,
    assertString,
} from '../../validators';
import { Config } from '../../video-data/config';
import { addAssetToContainer } from '../../libs/buildChildrenForScene';
import { NumberArray } from '../NumberArray';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { AlignChildren } from '../../types';
import { getAttributesToSerialize, NumberAttribute, BooleanAttribute } from '../attributes';
import { ErrorReporter } from '../../libs/error-reporter';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import type { FancyCaptionAsset } from './FancyCaptionAsset/asset/FancyCaptionAsset';
import type { TextAsset } from './TextAsset/TextAsset';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';
import type { Asset, AssetType, GetAssetType, GetAssetsOptions } from './Container';

const DEFAULT_MAX_HEIGHT_PROPORTION = 50;
const DEFAULT_CHILD_ALIGNMENT_OFFSET = 0;

type AssetGroupSettings = BaseAssetSettings & {
    name?: string;
};

/**
 * An AssetGroup Object allows us to control the position, dimensions and animation of its children.
 *
 * By controlling the children's position, we can organize them spacially, aligning their children horizontally (left,
 * center, right) and stack them vertically with a specific offset.
 *
 * We can control their dimensions by demanding their combined height never to exceed our maximum height. If the
 * AssetGroup contains TextAssets, their height is distributed, depending on their individual max-height property
 * and text content. This behaviour allows us to maximize the dimensions of one TextAsset with content when all others
 * are empty.
 *
 * We can organize their timing into relation to each other by transforming their animation. All children's animations
 * can start at the same time or sequentially. It also allows us to apply an animation offset to each child and control
 * this for both the intro and outro animation.
 *
 * To decorate our group we use dynamic styling.
 */
export class AssetGroup extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.GROUP_ASSET_TYPE;

    #maxHeight = 500;
    #alignChildren: AlignChildren = Config.LEFT_ALIGNMENT;
    #spaceBetweenChildren = 10;
    #childMaxHeightProportions = new NumberArray({
        minimum: 1,
        parent: this,
        redrawParentOnUpdate: true,
    });
    #childAlignmentOffset = new NumberArray({
        parent: this,
        redrawParentOnUpdate: true,
    });

    /**
     * Offset applied to each child's intro animation. We do this by apply the offset to each child's animationManager's
     * animation. This value is expressed in milliseconds.
     */
    @NumberAttribute()
    accessor childIntroAnimationOffset = 0;

    /**
     * Offset applied to each child's outro animation. We do this by apply the offset to each child's animationManager's
     * animation. This value is expressed in milliseconds.
     */
    @NumberAttribute()
    accessor childOutroAnimationOffset = 0;

    @BooleanAttribute()
    accessor hasUserChangedMaxWidth = false;

    #introAnimationMode: (typeof Config.ASSET_GROUP_ANIMATION_MODES)[number] =
        Config.SEQUENTIAL_TOP_DOWN_ANIMATION_MODE;
    #outroAnimationMode: (typeof Config.ASSET_GROUP_ANIMATION_MODES)[number] =
        Config.AT_THE_SAME_TIME_ANIMATION_MODE;

    override controlsChildPosition = true;

    // The asset group scales child animations to fit into the first 1/2 of the scene. However duration, wherever it is
    // set, is ultimately stored in the timeline and is in a sense 'global'. No 'build' will be triggered on the
    // asset group which is required to reset and rescale the animations. Here we keep track of the last known duration,
    // and if it changes, then a requiresRedraw = true is set during the _checkAndFixProperties.
    #previousDuration = -1;

    _moveContainer: BaseAsset;

    constructor(container: any, settings: AssetGroupSettings) {
        super(container, settings);

        // hierarchy
        this._moveContainer = new BaseAsset(container, {
            updateParentOnRedraw: true,
            autoDraw: settings.autoDraw,
            name: (settings.name ?? 'AssetGroup') + '_move',
            fetchResources: false,
        });
        this._addInternalChild(this._moveContainer._pixiContainer);
    }

    override get isLoaded(): boolean {
        return this.getAssets({ deep: false }).every((child) => child.isLoaded);
    }

    override get isDoneTryingToLoad(): boolean {
        return this.getAssets({ deep: false }).every((child) => child.isDoneTryingToLoad);
    }

    override get canPlay(): boolean {
        return this.getAssets({ deep: false }).every((child) => child.canPlay);
    }

    override get shouldLoad() {
        return true;
    }

    /**
     * Approximates the biggest possible bounds for the asset group. As an approximation, it only
     * takes into account children which specify their own maxWidth. There are cases such as an asset group
     * containing styles that are not accounted for (e.g. assets that can exceed the group's max height)
     *
     * @returns {object} { x, width, y, height }. Each property is a number in the same units
     *  as luminary's resolution
     */
    override get approximateMaxBounds() {
        const allChildren = this.getAssets();
        if (!allChildren.length) {
            return {
                x: 0,
                width: 0,
                y: 0,
                height: 0,
            } as PIXI.Rectangle;
        }

        const computedMaxWidth = allChildren.reduce((maxWidth, childAsset) => {
            return typeof (childAsset as any)['maxWidth'] === 'number'
                ? Math.max(maxWidth, (childAsset as any)['maxWidth'])
                : maxWidth;
        }, 0);

        const bounds = this.getBounds();
        const maxWidth = Math.max(bounds.width, computedMaxWidth);
        const maxHeight = Math.max(bounds.height, this.maxHeight);

        // get the transformation for the asset that controls our position
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(transformation, maxWidth, maxHeight);

        return {
            x,
            width: maxWidth,
            y,
            height: maxHeight,
        } as PIXI.Rectangle;
    }

    override getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        return this._moveContainer.getAssets(options);
    }

    override _addAsset(child: BaseAsset) {
        this._addAssetAt(child, this._moveContainer._pixiContainer.children.length);
        return child;
    }

    override _addAssetAt(child: BaseAsset, index: number) {
        // parent child
        if (!(child instanceof BaseAsset)) {
            this._moveContainer._addAssetAt(child, index);
            return;
        }
        const initLocalAutoDraw = this.autoDraw;
        this.autoDraw = false;

        this._moveContainer._addAssetAt(child, index);
        this.#childMaxHeightProportions.addNumberAtIndex(DEFAULT_MAX_HEIGHT_PROPORTION, index);
        this.#childAlignmentOffset.addNumberAtIndex(DEFAULT_CHILD_ALIGNMENT_OFFSET, index);

        this.autoDraw = initLocalAutoDraw;
    }

    /**
     * Returns the index of the asset in the AssetGroup. Since the AssetGroup is a collection of assets and graphics, the child and asset index can be different.
     */
    getAssetIndex(child: BaseAsset): number {
        const childAssetIndex = this.getAssets({ deep: false }).findIndex(
            (asset) => asset === child,
        );
        if (childAssetIndex < 0) {
            ErrorReporter.setContext('assetNotAChild', {
                parentId: this.id,
                parentName: this.name,
                parentType: this.classType,
                childId: child.id,
                childName: child.name,
                childType: child.classType,
            });
            throw new Error('Asset not a child');
        }
        return childAssetIndex;
    }

    adjustAllAssetMaxWidthsToFitResolution(offset: number = 0): void {
        const { x: currentMaxTextX, width: currentMaxTextWidth } = this.approximateMaxBounds;
        const resolutionWidth = this.root.referenceResolution.width;

        // When we have a graphic on the group level (i.e. a box), we also want to include its
        // bounds in the offset when shrinking the maxWidth, to ensure that they do not get cut off by the canvas.
        const currentTotalWidthOfAssetsInGroup = this.#calculateCurrentTotalWidthOfSubAssets();
        offset +=
            currentTotalWidthOfAssetsInGroup > 0
                ? Math.abs(this.getBounds().width - currentTotalWidthOfAssetsInGroup)
                : 0;

        if (!(currentMaxTextX < 0 || currentMaxTextX + currentMaxTextWidth > resolutionWidth)) {
            return;
        }

        this.getAssets({ deep: false }).forEach((asset) => {
            if (Config.isTextAsset(asset)) {
                asset.adjustMaxWidthToFitResolution(offset);
                return;
            }

            if (Config.isAssetGroup(asset)) {
                asset.getAssets({ deep: false }).forEach((nestedAsset) => {
                    if (Config.isTextAsset(nestedAsset)) {
                        nestedAsset.adjustMaxWidthToFitResolution(offset);
                    }
                });
            }
        });
    }

    #calculateCurrentTotalWidthOfSubAssets(): number {
        const allAssets = this.getAssets({ deep: true });
        if (allAssets.length === 0) {
            return 0;
        }
        const { minX, maxX } = allAssets.reduce(
            (acc, asset) => {
                const { x, width } = asset.getBounds();
                return {
                    minX: Math.min(acc.minX, x),
                    maxX: Math.max(acc.maxX, x + width),
                };
            },
            { minX: Infinity, maxX: -Infinity },
        );
        return maxX - minX;
    }

    /**
     * Returns the largest text asset in the AssetGroup based on width.
     */
    getLargestTextAsset(): Asset | null {
        const allAssetsWithText = [
            ...this.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: true }),
            ...this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE, { deep: true }),
        ];
        if (allAssetsWithText.length === 0) {
            return null;
        }

        let largestAsset: TextAsset | FancyCaptionAsset | null =
            allAssetsWithText.length > 0 ? allAssetsWithText[0] : null;
        let largestWidth = largestAsset ? largestAsset.getBounds().width : 0;

        allAssetsWithText.forEach((asset) => {
            const currentTextWidth = asset.getBounds().width;
            if (Config.isTextAsset(asset) && asset.hasText && currentTextWidth > largestWidth) {
                largestWidth = currentTextWidth;
                largestAsset = asset;
            }
            // TODO: Using the maxWidth property for fancy captions as an interim solution,
            // until we can provide a more polished solution for fancy captions inside of asset groups.
            else if (Config.isFancyCaptionAsset(asset) && asset.maxWidth > largestWidth) {
                largestWidth = currentTextWidth;
                largestAsset = asset;
            }
        });
        return largestAsset;
    }

    /**
     * Moves the asset to the specified index in the AssetGroup (taking any graphics/children into account).
     */
    moveAsset(child: BaseAsset, index: number) {
        const assetCount = this.getAssets({ deep: false }).length;
        assertMinimum(index, 0);
        assertMaximum(index, assetCount - 1);
        const assetOffset = this._moveContainer._pixiContainer.children.length - assetCount;
        const currentIndex = this.getAssetIndex(child);

        // move child
        this._moveContainer.moveChild(child, assetOffset + index);

        // update asset properties
        this.#childMaxHeightProportions.moveIndex(currentIndex, index);
        this.#childAlignmentOffset.moveIndex(currentIndex, index);

        return child;
    }

    override getChildIndex(child: BaseAsset | PIXI.DisplayObject): number {
        return this._moveContainer.getChildIndex(child);
    }

    override moveChild(child: BaseAsset | PIXI.DisplayObject, index: number) {
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;
        const currentIndex = this._moveContainer._pixiContainer.children.indexOf(child);

        const result = this._moveContainer.moveChild(child, index);
        if (child instanceof BaseAsset) {
            this.#childMaxHeightProportions.moveIndex(currentIndex, index);
            this.#childAlignmentOffset.moveIndex(currentIndex, index);
        }

        this.autoDraw = initAutoDraw;
        return result;
    }

    override removeChild(child: BaseAsset) {
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        const index = this.getAssetIndex(child);
        this._moveContainer.removeChild(child);
        if (child instanceof BaseAsset) {
            this.#childMaxHeightProportions.removeIndex(index);
            this.#childAlignmentOffset.removeIndex(index);
        }

        this.autoDraw = initAutoDraw;

        return child;
    }

    /**
     * The maximum height proportion for each child. We use this value to calculate the  `maxHeight` value for each
     * child, by multiplying its proportion within the combined `childMaxHeightProportions`, with the AssetGroups's
     * `maxHeight`, minus `spaceBetweenChildren`. If a child is a TextAsset without any content, then its proportion is
     * automatically zero, allowing the extra height to be distributed between the other children.
     */
    get childMaxHeightProportions() {
        return this.#childMaxHeightProportions;
    }

    /**
     * Loads childMaxHeightProportions while making sure the array length is not greater than the amount of children.
     */
    _childMaxHeightProportionsCustomLoad(value: number[]) {
        assertArray(value);
        const numberOfChildren = this.getAssets({ deep: false }).length;
        this.#childMaxHeightProportions.load(value.slice(0, numberOfChildren));
    }

    /**
     * The intro animation transformation mode for all children. This mode defines how to transform the incoming or
     * intro animation of each child. Do we want them to start simultaneously, or do we want them to animate
     * sequentially? If sequentially, do want to start at the top or the bottom. The valid options are defined in
     * Config.ASSET_GROUP_ANIMATION_MODES.
     */
    get introAnimationMode() {
        return this.#introAnimationMode;
    }

    set introAnimationMode(value) {
        if (this.introAnimationMode === value) {
            return;
        }
        assertString(value);
        assertInArray(value, Config.ASSET_GROUP_ANIMATION_MODES);
        this.#introAnimationMode = value;
        this.requiresRedraw = true;
    }

    /**
     * The outro animation transformation mode for all children. This mode defines how to transform the outgoing or
     * outro animation of each child. Do we want them to start simultaneously, or do we want them to animate
     * sequentially? If sequentially, do want to start at the top or the bottom. The valid options are defined in
     * Config.ASSET_GROUP_ANIMATION_MODES.
     */
    get outroAnimationMode() {
        return this.#outroAnimationMode;
    }

    set outroAnimationMode(value) {
        if (this.outroAnimationMode === value) {
            return;
        }
        assertString(value);
        assertInArray(value, Config.ASSET_GROUP_ANIMATION_MODES);
        this.#outroAnimationMode = value;
        this.requiresRedraw = true;
    }

    /**
     * The maximum height for our children.
     */
    get maxHeight(): number {
        return this.#maxHeight;
    }

    set maxHeight(value: number) {
        if (this.maxHeight === value) {
            return;
        }
        assertNumber(value);
        this.#maxHeight = value;
        this.requiresRedraw = true;
    }

    /**
     * The horizontal alignment option for our children
     */
    get alignChildren(): AlignChildren {
        return this.#alignChildren;
    }

    set alignChildren(value) {
        if (this.alignChildren === value) {
            return;
        }
        assertString(value);
        this.#alignChildren = value;
        this.requiresRedraw = true;
    }

    applyAlignmentRecursively(alignment: AlignChildren): void {
        this.alignChildren = alignment;
        this.getAssets({ deep: false }).forEach((asset) => {
            if (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset)) {
                asset.textAlign =
                    TextAlignment[alignment.toUpperCase() as keyof typeof TextAlignment];
            } else if (Config.isAssetGroup(asset)) {
                asset.applyAlignmentRecursively(alignment);
            }
        });
    }

    /**
     * The horizontal alignment offset for each child
     */
    get childAlignmentOffset(): NumberArray {
        return this.#childAlignmentOffset;
    }

    get spaceBetweenChildren() {
        return this.#spaceBetweenChildren;
    }

    /**
     * The vertical space between each child.
     */
    set spaceBetweenChildren(value: number) {
        if (this.spaceBetweenChildren === value) {
            return;
        }
        assertNumber(value);
        this.#spaceBetweenChildren = value;
        this.requiresRedraw = true;
    }

    override get boundsWithoutAnimation(): PIXI.Rectangle {
        const assets = this.getAssets();

        const consistsOnlyOfEmptyTexts = assets.every((asset) => {
            if (!Config.isAnyTextAsset(asset)) {
                return false;
            }
            if (!asset.hasText) {
                return true;
            }
            return false;
        });

        if (consistsOnlyOfEmptyTexts) {
            // The bounds will be an empty rectangle centered at (0,0) if all assets are empty text assets.
            // But returning a rectangle that's centered according to our transformation will help
            // place NTE's empty state box in a more accurate place on screen.
            const transformation = this.getFreelyPositionedParent().transformation;
            const { x, y } = transformation.position;
            return new PIXI.Rectangle(x, y, 0, 0);
        } else {
            return super.boundsWithoutAnimation;
        }
    }

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        const bounds = this.getBounds();
        const position = getPositionFromTransformation(
            this.transformation,
            bounds.width,
            bounds.height,
        );
        // move to origin + transformation
        this.x = bounds.x * -1 + position.x + offsetX;
        this.y = bounds.y * -1 + position.y + offsetY;
        this.rotation = this.transformation.rotation + offsetRotation;
    }

    /**
     * Add a brand new asset of type assetType to the AssetGroup
     */
    addAsset<T extends AssetType>(assetType: T, settings?: Record<string, any>): GetAssetType<T>;
    /**
     * @param data - object created by asset.save()
     */
    addAsset(data: Record<string, any>, settings?: Record<string, any>): GetAssetType<unknown>;
    addAsset(assetTypeOrData: AssetType | Record<string, any>, settings: Record<string, any> = {}) {
        if (typeof assetTypeOrData === 'string') {
            return addAssetToContainer(this, assetTypeOrData, settings);
        }
        if (assetTypeOrData.id) {
            settings = { ...settings, id: assetTypeOrData.id };
        }
        if (assetTypeOrData.name) {
            settings = { ...settings, name: assetTypeOrData.name };
        }
        const asset = addAssetToContainer(this, assetTypeOrData.classType, settings);
        asset.load(assetTypeOrData);
        return asset;
    }

    /**
     *
     * @inheritdoc
     */
    override saveWithNestedAssets(): OpaqueAsset[] {
        return [this.save(), ...this.getAssets().map((child) => child.save())];
    }

    override _build() {
        // reset
        this.clearAnimation();
        this._resetChildrenAnimation();
        this.resetTransformation();
        this._moveChildrenToOrigin();

        // position
        this._distributeMaxHeightBetweenChildren();
        this._alignChildren();
        this._applyAlignmentOffsetToChildren();
        this._moveChildrenOneBelowTheOther();

        this._prepareDynamicStylingForUpdate();
        this.applyTransformation();
        this._updateDynamicStyling();

        // animation
        if (this._childrenHaveAnimationOfType(Config.START_ANIMATION_CURVE_ANCHOR)) {
            this._applyIntroAnimationModeToChildren();
            this._scaleDownIntroAnimationToMaxDuration();
        }
        if (this._childrenHaveAnimationOfType(Config.END_ANIMATION_CURVE_ANCHOR)) {
            this._applyOutroAnimationModeToChildren();
            this._scaleDownOutroAnimationToMaxDuration();
        }

        this.applyIntroAnimations();
        this.applyOutroAnimations();
        this.animationManager.setInitValues();

        // It's often possible that we have wrong child bounds after all these operations
        // We can remove this line after moving to CanvasText.
        this._cacheBoundsAfterBuildRecursively();
    }

    /**
     * Move the children to the origin (0/0) so they are in the top left/top corner of the current or local container
     */
    _moveChildrenToOrigin() {
        this.getAssets({ deep: false }).forEach((child) => {
            child.transformation.anchor.x = 0;
            child.transformation.anchor.y = 0;
            child.x = 0;
            child.y = 0;

            // Not all assets have their bounds in the top-left corner. For those, we must offset the position. Our
            // circle asset is a great example of this. Here the top-left corner is in the center of the circle so we
            // must offset the position in both x and y by its radius for the bounds to end up in the top-left corner.
            const { x, y } = child.getLocalBounds();

            // Note: Updating a container's x and y property doesn't update the internal transformation matrices
            // It's only because PIXI calls updateTransform as part of getLocalBounds, that they get updated.
            if (x !== 0) child.x -= x;
            if (y !== 0) child.y -= y;
        });
    }

    _distributeMaxHeightBetweenChildren() {
        const children = this.getAssets({ deep: false });
        if (children.length === 0) {
            return;
        }
        const childrenTextAssets = children.filter(Config.isAnyTextAsset);
        if (childrenTextAssets.length === 0) {
            return;
        }

        // set the max height of all text assets that don't have any text to 0
        childrenTextAssets
            .filter(
                (child) =>
                    !child.hasText &&
                    !(Config.isFancyCaptionAsset(child) || Config.isCaptionAsset(child)),
            )
            .forEach((child) => {
                child.maxHeight = 0;
            });

        // get max height we allocate to our text assets
        const totalNonTextAssetHeight = sum(
            children
                .filter((child) => !Config.isAnyTextAsset(child))
                .map((child) => child.getLocalBounds().height),
        );
        const remainingHeight = Math.max(this.maxHeight - totalNonTextAssetHeight, 0);

        // distribute the remaining max height among our text assets
        const childrenWithText = childrenTextAssets.filter((child) => child.hasText);
        const proportionsToDistribute: number[] = [];
        this.#childMaxHeightProportions.array.forEach((value, index) => {
            const child = children[index];
            if (Config.isAnyTextAsset(child) && child.hasText) {
                proportionsToDistribute.push(value);
            }
        });

        const heightToDistribute =
            remainingHeight - (childrenWithText.length - 1) * this.spaceBetweenChildren;
        const proportionsTotal = sum(proportionsToDistribute);
        const normalizedProportions =
            proportionsToDistribute.length > 0
                ? proportionsToDistribute.map((proportion) =>
                      proportionsTotal > 0 ? proportion / proportionsTotal : 0,
                  )
                : [];
        const finalTextAssetMaxHeights = normalizedProportions.map((proportion) =>
            Math.floor(proportion * heightToDistribute),
        );

        // Optimization!
        // Apply max height redistribution only if calculated maxHeights differ from current by more than one pixel
        if (
            childrenWithText.some(
                (child, index) =>
                    typeof finalTextAssetMaxHeights[index] === 'number' &&
                    Math.floor(child.maxHeight) !== finalTextAssetMaxHeights[index],
            )
        ) {
            childrenWithText.forEach((child, index) => {
                if (typeof finalTextAssetMaxHeights[index] === 'number') {
                    child.maxHeight = Math.max(0, finalTextAssetMaxHeights[index]);
                }
            });
        }
    }

    _alignChildren() {
        const children = this.getAssets({ deep: false });
        if (children.length < 2) {
            return;
        }
        const childrenBounds = children.map((child) => child.getLocalBounds());
        const maxChildWidth = Math.max(...childrenBounds.map((child) => child.width));
        switch (this.alignChildren) {
            case 'left':
                // nothing to do here
                break;
            case 'center':
                children.forEach((child, index) => {
                    const offset = (maxChildWidth - childrenBounds[index].width) / 2;
                    child.x += offset;
                });
                break;
            case 'right':
                children.forEach((child, index) => {
                    const offset = maxChildWidth - childrenBounds[index].width;
                    child.x += offset;
                });
                break;
            default:
                throw Error(`Unsupported alignChildren value of ${this.alignChildren}`);
        }
    }

    _applyAlignmentOffsetToChildren() {
        this.getAssets({ deep: false }).forEach((child, index) => {
            child.x += this.childAlignmentOffset.getValueAtIndex(index);
        });
    }

    _moveChildrenOneBelowTheOther() {
        let yOffset = 0;
        this.getAssets({ deep: false })
            .filter(
                (child) =>
                    !Config.isAnyTextAsset(child) ||
                    (Config.isAnyTextAsset(child) && child.hasText),
            )
            .forEach((child) => {
                const { height } = child.getLocalBounds();
                child.y += yOffset;
                yOffset += height;
                yOffset += this.spaceBetweenChildren;
            });
    }

    _resetChildrenAnimation() {
        this.getAssets({ deep: false }).forEach((child) => {
            // reset child animation to their default value
            child.animationManager.reset();
            child.animationManager.resetToInitValues();
        });
    }

    _childrenHaveAnimationOfType(anchorType = Config.START_ANIMATION_CURVE_ANCHOR) {
        return this.getAssets({ deep: false }).some((child) =>
            child.animationManager.hasAnchors(anchorType),
        );
    }

    _applyIntroAnimationModeToChildren() {
        let normalizedTimeOffset = this.childIntroAnimationOffset / this.duration;
        if (this.introAnimationMode === Config.SEQUENTIAL_TOP_DOWN_ANIMATION_MODE) {
            // move the animation of one asset, after the other
            this.getAssets({ deep: false }).forEach((child) => {
                const normalizedChildIntroDuration =
                    child.animationManager.introDuration / this.duration;
                child.animationManager.moveAllGroups(
                    normalizedTimeOffset,
                    0,
                    Config.START_ANIMATION_CURVE_ANCHOR,
                );
                normalizedTimeOffset += normalizedChildIntroDuration;
            });
        } else if (this.introAnimationMode === Config.SEQUENTIAL_BOTTOM_UP_ANIMATION_MODE) {
            // move the animation of one asset, after the other
            this.getAssets({ deep: false })
                .reverse()
                .forEach((child) => {
                    const normalizedChildIntroDuration =
                        child.animationManager.introEnd / this.duration;
                    child.animationManager.moveAllGroups(
                        normalizedTimeOffset,
                        0,
                        Config.START_ANIMATION_CURVE_ANCHOR,
                    );
                    normalizedTimeOffset += normalizedChildIntroDuration;
                });
        } else if (this.introAnimationMode === Config.AT_THE_SAME_TIME_ANIMATION_MODE) {
            this.getAssets({ deep: false }).forEach((child) => {
                child.animationManager.moveAllGroups(
                    normalizedTimeOffset,
                    0,
                    Config.START_ANIMATION_CURVE_ANCHOR,
                );
            });
        }
    }

    _scaleDownIntroAnimationToMaxDuration() {
        // ensure the animation doesn't take up more then Config.MAX_ANIMATION_DURATION_PERCENTAGE of our duration
        const assets = this.getAssets({ deep: false });
        if (assets.length === 0) return;

        const totalAnimationDuration = Math.max(
            ...assets.map((child) => child.animationManager.introDuration),
        );
        const allowedDuration = this.duration * Config.MAX_ANIMATION_DURATION_PERCENTAGE;
        const animationScale =
            totalAnimationDuration > 0 ? allowedDuration / totalAnimationDuration : 1.0;
        if (animationScale < 1.0) {
            assets.forEach((child) => {
                child.animationManager.scaleAllGroups(
                    animationScale,
                    Config.START_ANIMATION_CURVE_ANCHOR,
                );
            });
        }
    }

    _applyOutroAnimationModeToChildren() {
        const normalizedGlobalTimeOffset = this.childOutroAnimationOffset / this.duration;

        const childrenWithOutroAnimations = this.getAssets({ deep: false }).filter((child) =>
            child.animationManager.hasAnchors(Config.END_ANIMATION_CURVE_ANCHOR),
        );
        const totalDurationOfAllChildren = sum(
            childrenWithOutroAnimations.map((child) => child.animationManager.outroDuration),
        );
        let normalizedTimeOffset = totalDurationOfAllChildren / this.duration;

        normalizedTimeOffset += normalizedGlobalTimeOffset;
        if (this.outroAnimationMode === Config.SEQUENTIAL_TOP_DOWN_ANIMATION_MODE) {
            childrenWithOutroAnimations.forEach((child) => {
                const normalizedChildOutroDuration =
                    child.animationManager.outroDuration / this.duration;
                normalizedTimeOffset -= normalizedChildOutroDuration;
                child.animationManager.moveAllGroups(
                    -normalizedTimeOffset,
                    0,
                    Config.END_ANIMATION_CURVE_ANCHOR,
                );
            });
        } else if (this.outroAnimationMode === Config.SEQUENTIAL_BOTTOM_UP_ANIMATION_MODE) {
            // move the animation of one asset, before the other
            childrenWithOutroAnimations.reverse().forEach((child) => {
                const normalizedChildOutroDuration =
                    child.animationManager.outroDuration / this.duration;
                normalizedTimeOffset -= normalizedChildOutroDuration;
                child.animationManager.moveAllGroups(
                    -normalizedTimeOffset,
                    0,
                    Config.END_ANIMATION_CURVE_ANCHOR,
                );
            });
        } else if (this.outroAnimationMode === Config.AT_THE_SAME_TIME_ANIMATION_MODE) {
            this.getAssets({ deep: false }).forEach((child) => {
                child.animationManager.moveAllGroups(
                    -normalizedGlobalTimeOffset,
                    0,
                    Config.END_ANIMATION_CURVE_ANCHOR,
                );
            });
        }
    }

    _scaleDownOutroAnimationToMaxDuration() {
        const assets = this.getAssets({ deep: false });
        if (assets.length === 0) return;

        const totalAnimationDuration = Math.max(
            ...assets.map((child) => child.animationManager.outroDuration),
        );
        const allowedDuration = this.duration * Config.MAX_ANIMATION_DURATION_PERCENTAGE;
        const animationScale = allowedDuration / totalAnimationDuration;
        if (animationScale < 1.0) {
            assets.forEach((child) => {
                child.animationManager.scaleAllGroups(
                    animationScale,
                    Config.END_ANIMATION_CURVE_ANCHOR,
                );
            });
        }
    }

    get duration(): number {
        return this.container.duration;
    }

    override async _bufferAtTime(time: number, bufferingMode: BufferingMode) {
        const children = this.getAssets({ deep: false });
        for (let i = 0; i < children.length; i++) {
            await children[i]._bufferAtTime(time, bufferingMode);
        }
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            ...getAttributesToSerialize(this),
            'maxHeight',
            'childMaxHeightProportions',
            'introAnimationMode',
            'outroAnimationMode',
            'alignChildren',
            'childAlignmentOffset',
            'spaceBetweenChildren',
            'hasUserChangedMaxWidth',
        ]);
    }

    override getCreationContainersAtLevelForType(
        level: DynamicStylingCreationLevel,
        type = CreationType.Animation,
    ): PIXI.Container[] {
        assertInEnum(type, CreationType);
        switch (level) {
            case DynamicStylingCreationLevel.Object:
                return [this._moveContainer];
            default:
                console.warn(`Unsupported container of type ${level}`);
        }
        return [];
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        if (this.slide && this.#previousDuration !== this.slide.duration) {
            this.#previousDuration = this.slide.duration;
            this.requiresRedraw = true;
        }
    }
}
